import { type UseMutationResult } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useToast } from "@vega-core/components/Toast/useToast";
import { MAX_FILE_SIZE } from "@vega-core/constants/global";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { formatFileSize } from "@vega-core/utils/formatNumber";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseDealCsvUploadParams<TData = unknown, TVariables = unknown> = {
  dealId: string;
  uploadMutation: UseMutationResult<
    TData,
    ErrorType<unknown>,
    TVariables,
    unknown
  >;
  inputElementId: string;
  onUploadSuccess?: () => void;
};

export const useDealCsvUpload = <TData = unknown, TVariables = unknown>({
  dealId,
  uploadMutation,
  inputElementId,
  onUploadSuccess,
}: UseDealCsvUploadParams<TData, TVariables>) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const {
    isOpen: isUploading,
    setTrue: setIsUploadingTrue,
    setFalse: setIsUploadingFalse,
  } = useBoolean(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.type !== "text/csv") {
      showToast({
        title: m.status_error(),
        description: m.coInvestments_error_uploadCsv_notCsv(),
        variant: "destructive",
      });

      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast({
        title: m.status_error(),
        description: m.errors_fileSizeTooLarge_single({
          maxSizeMB: formatFileSize(MAX_FILE_SIZE),
        }),
        variant: "destructive",
      });

      return;
    }

    setIsUploadingTrue();

    uploadMutation.mutate(
      {
        dealId,
        data: { file },
      } as TVariables,
      {
        onSuccess: () => {
          setIsUploadingFalse();
          showToast({
            title: m.status_success(),
            variant: "success",
          });
          onUploadSuccess?.();
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_error_uploadCsv(),
            statusCodeMessages: {
              413: {
                message: m.errors_fileSizeTooLarge_single({
                  maxSizeMB: formatFileSize(MAX_FILE_SIZE),
                }),
              },
            },
          });
          setIsUploadingFalse();
        },
      },
    );

    event.target.value = "";
  };

  const handleUploadClick = () => {
    const input = document.getElementById(
      inputElementId,
    ) as HTMLInputElement | null;
    input?.click();
  };

  return {
    isUploading,
    handleFileUpload,
    handleUploadClick,
  };
};
